import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SlidersHorizontal, Save, Loader2, Gauge, Factory } from "lucide-react";
import { toast } from "sonner";

const PROD_CATS = [
  { key: "auto_tanques", label: "Auto Tanques" },
  { key: "estaciones", label: "Estaciones" },
  { key: "cilindreras", label: "Cilindreras" },
];
const PROD_CLASSES = ["A", "B", "C"];
const CLASS_COLORS = { A: "#1f2a44", B: "#2b9fd6", C: "#b01e3c" };
const OPS = [
  { key: "mayor", label: "Mayor a" },
  { key: "mayor_igual", label: "Mayor o igual a" },
  { key: "menor", label: "Menor a" },
  { key: "menor_igual", label: "Menor o igual a" },
  { key: "entre", label: "Entre" },
  { key: "igual", label: "Igual a" },
];

const numFmt = (n) => (n === "" || n == null ? "?" : Number.isInteger(Number(n)) ? Number(n) : Math.round(Number(n) * 10) / 10);

function ruleLabel(r) {
  if (!r || !r.op) return "";
  const v1 = numFmt(r.v1);
  const v2 = numFmt(r.v2);
  switch (r.op) {
    case "mayor": return `Mayor a ${v1} TON`;
    case "mayor_igual": return `Mayor o igual a ${v1} TON`;
    case "menor": return `Menor a ${v1} TON`;
    case "menor_igual": return `Menor o igual a ${v1} TON`;
    case "igual": return `Igual a ${v1} TON`;
    case "entre": {
      const a = Number(r.v1), b = Number(r.v2);
      if (Number.isNaN(a) || Number.isNaN(b)) return `Entre ${v2} y ${v1} TON`;
      return `Entre ${numFmt(Math.max(a, b))} y ${numFmt(Math.min(a, b))} TON`;
    }
    default: return "";
  }
}

export default function ParametrosOperativos() {
  const qc = useQueryClient();
  const [plant, setPlant] = useState("");
  const [rules, setRules] = useState({});

  const { data: plants, isLoading: plantsLoading } = useQuery({
    queryKey: ["operativo-plants"],
    queryFn: async () => (await api.get("/operativo/plants")).data,
  });

  const { data: loaded, isFetching } = useQuery({
    queryKey: ["operativo-rules", plant],
    queryFn: async () => (await api.get("/operativo/prod-rules", { params: { plant } })).data,
    enabled: !!plant,
  });

  useEffect(() => { if (loaded?.rules) setRules(loaded.rules); }, [loaded]);

  const saveRules = useMutation({
    mutationFn: async () => (await api.put("/operativo/prod-rules", { plant, rules })).data,
    onSuccess: () => {
      toast.success("Reglas de la planta guardadas");
      qc.invalidateQueries({ queryKey: ["operativo-rules", plant] });
      qc.invalidateQueries({ queryKey: ["report"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const setRule = (ck, cls, field, value) =>
    setRules((r) => ({
      ...r,
      [ck]: { ...(r[ck] || {}), [cls]: { ...((r[ck] || {})[cls] || {}), [field]: field.startsWith("v") ? (value === "" ? "" : Number(value)) : value } },
    }));

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">Configuración operativa</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight flex items-center gap-2">
          <SlidersHorizontal className="w-8 h-8 text-red-500" /> Parámetros Operativos
        </h1>
        <p className="text-slate-400 text-sm mt-1">Configura las reglas de Productividades por planta. Solo ves las plantas a las que tienes acceso.</p>
      </div>

      <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-6 shadow-lg">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div className="min-w-[260px]">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5"><Factory className="w-3.5 h-3.5" /> Planta</p>
            {plantsLoading ? (
              <div className="h-9 flex items-center"><Loader2 className="w-4 h-4 text-red-600 animate-spin" /></div>
            ) : (
              <select value={plant} onChange={(e) => setPlant(e.target.value)} data-testid="operativo-plant-select"
                className="h-10 w-full rounded bg-slate-950/60 border border-slate-700 text-white text-sm px-3">
                <option value="">Selecciona una planta…</option>
                {(plants || []).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
          </div>
          <Button onClick={() => saveRules.mutate()} disabled={!plant || saveRules.isPending}
            className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-operativo-rules-button">
            {saveRules.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Guardar</>}
          </Button>
        </div>

        {!plant ? (
          <div className="text-slate-500 text-sm py-10 text-center" data-testid="operativo-empty">Selecciona una planta para editar sus reglas de clasificación.</div>
        ) : isFetching ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
        ) : (
          <div>
            <h2 className="font-display text-lg font-bold text-slate-100 flex items-center gap-2 mb-1"><Gauge className="w-5 h-5 text-red-500" /> Reglas de Productividades — {plant}</h2>
            <p className="text-xs text-slate-500 mb-4">
              {loaded?.custom ? "Esta planta tiene reglas personalizadas." : "Usando las reglas por defecto. Al guardar se crean reglas específicas para esta planta."}
            </p>
            <div className="space-y-4">
              {PROD_CATS.map((cat) => (
                <div key={cat.key} className="bg-slate-900/40 border border-[#223048] rounded-lg p-4" data-testid={`op-rule-${cat.key}`}>
                  <p className="text-white font-semibold mb-3">{cat.label}</p>
                  <div className="space-y-2">
                    {PROD_CLASSES.map((cls) => {
                      const rc = (rules[cat.key] || {})[cls] || {};
                      return (
                        <div key={cls} className="flex flex-wrap items-center gap-2">
                          <span className="px-2.5 py-1.5 text-white text-xs font-bold rounded w-9 text-center" style={{ backgroundColor: CLASS_COLORS[cls] }}>"{cls}"</span>
                          <select value={rc.op || "mayor"} onChange={(e) => setRule(cat.key, cls, "op", e.target.value)}
                            data-testid={`op-op-${cat.key}-${cls}`} className="h-9 rounded bg-slate-950/60 border border-slate-700 text-white text-sm px-2 min-w-[140px]">
                            {OPS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                          </select>
                          <Input type="number" step="0.1" value={rc.v1 ?? ""} onChange={(e) => setRule(cat.key, cls, "v1", e.target.value)}
                            data-testid={`op-v1-${cat.key}-${cls}`} placeholder="TON" className="h-9 w-24 bg-slate-950/60 border-slate-700 text-white font-mono text-sm" />
                          {rc.op === "entre" && (
                            <>
                              <span className="text-slate-500 text-xs">y</span>
                              <Input type="number" step="0.1" value={rc.v2 ?? ""} onChange={(e) => setRule(cat.key, cls, "v2", e.target.value)}
                                data-testid={`op-v2-${cat.key}-${cls}`} placeholder="TON" className="h-9 w-24 bg-slate-950/60 border-slate-700 text-white font-mono text-sm" />
                            </>
                          )}
                          <span className="text-[11px] text-slate-400 ml-1">{ruleLabel(rc)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
